"use client";

import Link from "next/link";
import type { ComponentProps, MouseEvent } from "react";

import { trackEvent } from "@/lib/analytics";

type LinkProps = ComponentProps<typeof Link>;

type TrackedCTALinkProps = LinkProps & {
  /** Funnel source label for this CTA, e.g. the UTM source of the destination. */
  context: string;
  /** Optional locale tag for channel/segment breakdowns. */
  locale?: string;
  /** Override the analytics event name. Defaults to `cta_click`. */
  event?: string;
  /** Extra params merged into the fired event (e.g. `source` for `offer_bundle_cta`). */
  eventParams?: Record<string, string | number | boolean>;
};

function hrefToString(href: LinkProps["href"]): string {
  if (typeof href === "string") {
    return href;
  }
  return href.pathname ?? "";
}

/**
 * Drop-in replacement for next/link that fires a `cta_click` analytics event on
 * click. Lets server-rendered marketing pages instrument their CTAs without
 * becoming client components themselves. Pass `event` to fire a named funnel
 * event instead (e.g. `offer_bundle_cta` from the paid landing page).
 */
export function TrackedCTALink({
  context,
  locale,
  event,
  eventParams,
  href,
  onClick,
  ...props
}: TrackedCTALinkProps) {
  const target = hrefToString(href);

  return (
    <Link
      href={href}
      onClick={(clickEvent: MouseEvent<HTMLAnchorElement>) => {
        trackEvent(event ?? "cta_click", {
          context,
          target,
          ...(locale ? { locale } : {}),
          ...eventParams,
        });
        onClick?.(clickEvent);
      }}
      {...props}
    />
  );
}
